import React, { useEffect, useState, useCallback } from 'react';
import ReactFlow, {
  type Node,
  type Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  MarkerType,
  ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { api } from '../services/api';
import { Spinner, Alert } from '../components/ui';

// Node colors by type
const NODE_COLORS = {
  client: '#10B981',    // green
  server: '#3B82F6',    // blue
  tool: '#F59E0B',      // yellow/orange
  prompt: '#8B5CF6',    // purple
  resource: '#EC4899',  // pink
};

// Node shapes by type (using different styles)
const getNodeStyle = (type: string) => {
  const baseStyle = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '2px solid',
    borderColor: NODE_COLORS[type as keyof typeof NODE_COLORS] || '#666',
    background: 'white',
    fontSize: '12px',
    fontWeight: '500',
    color: '#1f2937',
    minWidth: '120px',
    textAlign: 'center' as const,
  };

  switch (type) {
    case 'client':
      return { ...baseStyle, borderRadius: '4px' }; // Square
    case 'server':
      return { ...baseStyle, borderRadius: '50%' }; // Circle
    case 'prompt':
      return { ...baseStyle, borderRadius: '8px', transform: 'rotate(45deg)' }; // Diamond (rotated)
    case 'tool':
      return { ...baseStyle, clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }; // Hexagon
    case 'resource':
      return { ...baseStyle, borderRadius: '12px', border: '3px dashed' }; // Rounded rect with dashed border
    default:
      return baseStyle;
  }
};

export const Graph: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);

  useEffect(() => {
    fetchGraphData();
  }, []);

  const fetchGraphData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.graph.getRelationships();

      // Convert backend nodes to React Flow nodes with hierarchical layout
      const flowNodes: Node[] = response.nodes.map((node) => {
        // Hierarchical layout: organize by type
        let x = 0;
        let y = 0;

        // Group by type in columns
        const clientNodes = response.nodes.filter(n => n.type === 'client');
        const serverNodes = response.nodes.filter(n => n.type === 'server');
        const toolNodes = response.nodes.filter(n => n.type === 'tool');
        const promptNodes = response.nodes.filter(n => n.type === 'prompt');
        const resourceNodes = response.nodes.filter(n => n.type === 'resource');

        switch (node.type) {
          case 'client':
            // Clients at the top center
            const clientIndex = clientNodes.indexOf(node);
            x = 700;
            y = 50 + (clientIndex * 200);
            break;
          case 'server':
            // Servers below clients, vertically stacked
            const serverIndex = serverNodes.indexOf(node);
            x = 700;
            y = 300 + (serverIndex * 250);
            break;
          case 'tool':
            // Tools to the right of servers, in a vertical grid
            const toolIndex = toolNodes.indexOf(node);
            const toolsPerColumn = 2; // 2 tools per column for better spacing
            const toolCol = Math.floor(toolIndex / toolsPerColumn);
            const toolRow = toolIndex % toolsPerColumn;
            x = 1100 + (toolCol * 280);
            y = 300 + (toolRow * 180);
            break;
          case 'prompt':
            // Prompts to the left of servers, vertically aligned
            const promptIndex = promptNodes.indexOf(node);
            const promptsPerColumn = 3;
            const promptCol = Math.floor(promptIndex / promptsPerColumn);
            const promptRow = promptIndex % promptsPerColumn;
            x = 200 - (promptCol * 250);
            y = 300 + (promptRow * 180);
            break;
          case 'resource':
            // Resources below prompts on the left side
            const resourceIndex = resourceNodes.indexOf(node);
            const resourcesPerColumn = 2;
            const resourceCol = Math.floor(resourceIndex / resourcesPerColumn);
            const resourceRow = resourceIndex % resourcesPerColumn;
            x = -100 - (resourceCol * 280);
            y = 300 + (resourceRow * 200);
            break;
        }

        return {
          id: node.id,
          type: 'default',
          position: { x, y },
          data: {
            label: (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {node.label}
                </div>
                <div style={{ fontSize: '10px', color: '#666' }}>
                  {node.type}
                </div>
              </div>
            ),
          },
          style: getNodeStyle(node.type),
        };
      });

      // Convert backend edges to React Flow edges
      const flowEdges: Edge[] = response.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: 'smoothstep', // Use smooth curved edges for better visibility
        label: edge.label,
        labelStyle: { fontSize: '11px', fill: '#333', fontWeight: '500' },
        labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
        labelBgPadding: [8, 4] as [number, number],
        labelBgBorderRadius: 4,
        animated: edge.type === 'prompt_tool', // Animate prompt-tool connections
        style: {
          stroke: NODE_COLORS[edge.type.split('_')[0] as keyof typeof NODE_COLORS] || '#999',
          strokeWidth: 3, // Thicker lines for better visibility
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: NODE_COLORS[edge.type.split('_')[0] as keyof typeof NODE_COLORS] || '#999',
        },
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
      setStats(response.stats);
    } catch (err) {
      console.error('Error fetching graph data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load graph data');
    } finally {
      setLoading(false);
    }
  };

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="danger" onClose={() => setError(null)}>
          {error}
        </Alert>
        <button
          onClick={fetchGraphData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Relationship Graph</h1>
            <p className="text-sm text-gray-600 mt-1">
              Visual representation of connections between clients, servers, tools, prompts, and resources
            </p>
          </div>
          {stats && (
            <div className="flex gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{stats.total_nodes}</div>
                <div className="text-gray-600">Nodes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{stats.total_edges}</div>
                <div className="text-gray-600">Connections</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '4px',
                border: `2px solid ${NODE_COLORS.client}`,
                background: 'white',
              }}
            />
            <span>Clients ({stats?.nodes_by_type.client || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                border: `2px solid ${NODE_COLORS.server}`,
                background: 'white',
              }}
            />
            <span>Servers ({stats?.nodes_by_type.server || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              style={{
                width: '16px',
                height: '16px',
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                border: `2px solid ${NODE_COLORS.tool}`,
                background: 'white',
              }}
            />
            <span>Tools ({stats?.nodes_by_type.tool || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '4px',
                border: `2px solid ${NODE_COLORS.prompt}`,
                background: 'white',
                transform: 'rotate(45deg)',
              }}
            />
            <span>Prompts ({stats?.nodes_by_type.prompt || 0})</span>
          </div>
        </div>
      </div>

      {/* Graph */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          connectionMode={ConnectionMode.Loose}
          fitView
          fitViewOptions={{ padding: 0.2 }}
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          <Controls />
        </ReactFlow>

        {/* Node Details Panel */}
        {selectedNode && (
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-4 w-64 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Node Details</h3>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-700">ID:</span>
                <p className="text-gray-600 truncate">{selectedNode.id}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Type:</span>
                <p className="text-gray-600">{selectedNode.data?.label}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
